import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LocalStorageKeys } from '@constants/enums';
import { selectProfileData } from '@pages/profile/store/selectors';
import { selectAuthState } from '@/layout/login/auth/selectors';
import { AppRoute, UseValidationStateReturn } from '../types';
import { REDIRECTION_REASONS } from '../constants';

export const useValidationState = (): UseValidationStateReturn => {
  const userAuth = useSelector(selectAuthState);
  const userProfile = useSelector(selectProfileData);

  const userEmail = useMemo(
    () =>
      userProfile?.naturalPerson?.email ||
      localStorage.getItem(LocalStorageKeys.USER_EMAIL) ||
      null,
    [userProfile?.naturalPerson?.email]
  );

  const isEmailConfirmed = !!userProfile?.is_email_confirmed;
  const hasAuthToken = !!userAuth.idToken || !!userAuth.token;

  const redirectionInfo = useMemo(() => {
    if (isEmailConfirmed) {
      return {
        shouldRedirect: true,
        targetRoute: '/' as AppRoute,
        reason: REDIRECTION_REASONS.EMAIL_CONFIRMED,
      };
    }

    if (!hasAuthToken) {
      return {
        shouldRedirect: true,
        targetRoute: '/' as AppRoute,
        reason: REDIRECTION_REASONS.NO_AUTH_TOKEN,
      };
    }

    return {
      shouldRedirect: false,
      targetRoute: null,
      reason: null,
    };
  }, [isEmailConfirmed, hasAuthToken]);

  return {
    userEmail,
    isEmailConfirmed,
    hasAuthToken,
    shouldRedirect: redirectionInfo.shouldRedirect,
    redirectionInfo,
  };
};

export default useValidationState;
